import React, { useEffect, useMemo, useRef, useState } from 'react';
import { UserCircle2, X } from 'lucide-react';
import './SettingsModal.css';

type SettingsSection = 'Graphics' | 'Audio' | 'Keyboard' | 'Game Play' | 'Language' | 'Tutorial & Tips';
const SETTINGS_SECTIONS: SettingsSection[] = ['Graphics', 'Audio', 'Keyboard', 'Game Play', 'Language', 'Tutorial & Tips'];

export interface GameSettings {
  graphics: {
    uiScalePct: number;
    reduceMotion: boolean;
  };
  audio: {
    masterVolume: number;
    objectMusic: number;
    music: number;
    voices: number;
    soundEffects: number;
    audioFocus: boolean;
    enableMoodSting: boolean;
    matchSpeedStereoMusic: boolean;
  };
  keyboard: {
    enabled: boolean;
  };
  gameplay: {
    aiThinkMinMs: number;
    showHints: boolean;
  };
  language: {
    code: string;
  };
  tutorial: {
    showTips: boolean;
  };
}

const STORAGE_KEY = 'omulongo_game_settings_v1';

function clampInt(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, Math.round(value)));
}

function defaultSettings(): GameSettings {
  return {
    graphics: {
      uiScalePct: 100,
      reduceMotion: false,
    },
    audio: {
      masterVolume: 60,
      objectMusic: 45,
      music: 80,
      voices: 100,
      soundEffects: 25,
      audioFocus: true,
      enableMoodSting: false,
      matchSpeedStereoMusic: true,
    },
    keyboard: {
      enabled: true,
    },
    gameplay: {
      aiThinkMinMs: 1000,
      showHints: true,
    },
    language: {
      code: 'en',
    },
    tutorial: {
      showTips: true,
    },
  };
}

export function loadGameSettings(): GameSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultSettings();
    const parsed = JSON.parse(raw) as Partial<GameSettings>;
    const base = defaultSettings();
    return {
      graphics: {
        ...base.graphics,
        ...(parsed.graphics || {}),
      },
      audio: {
        ...base.audio,
        ...(parsed.audio || {}),
      },
      keyboard: {
        ...base.keyboard,
        ...(parsed.keyboard || {}),
      },
      gameplay: {
        ...base.gameplay,
        ...(parsed.gameplay || {}),
      },
      language: {
        ...base.language,
        ...(parsed.language || {}),
      },
      tutorial: {
        ...base.tutorial,
        ...(parsed.tutorial || {}),
      },
    };
  } catch {
    return defaultSettings();
  }
}

export function saveGameSettings(settings: GameSettings): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
}

interface SettingsModalProps {
  open: boolean;
  userLabel: string;
  onClose: () => void;
  onApply?: (settings: GameSettings) => void;
}

function sectionButton(section: SettingsSection, active: boolean, onClick: () => void): JSX.Element {
  return (
    <button
      type="button"
      className={`settings-nav-item ${active ? 'active' : ''}`}
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
    >
      {section}
    </button>
  );
}

export default function SettingsModal({ open, userLabel, onClose, onApply }: SettingsModalProps): JSX.Element | null {
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const mobileDropdownRef = useRef<HTMLDivElement | null>(null);

  const persisted = useMemo(() => (open ? loadGameSettings() : null), [open]);
  const [section, setSection] = useState<SettingsSection>('Audio');
  const [draft, setDraft] = useState<GameSettings>(() => loadGameSettings());
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState<boolean>(false);

  useEffect(() => {
    if (!open) return;
    setSection('Audio');
    setDraft(loadGameSettings());
    setMobileDropdownOpen(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        if (mobileDropdownOpen) {
          setMobileDropdownOpen(false);
          return;
        }
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose, mobileDropdownOpen]);

  useEffect(() => {
    if (!open) return;
    if (!mobileDropdownOpen) return;
    const onDown = (event: MouseEvent | TouchEvent): void => {
      const target = event.target as Node | null;
      if (!target) return;
      if (mobileDropdownRef.current && mobileDropdownRef.current.contains(target)) return;
      setMobileDropdownOpen(false);
    };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('touchstart', onDown, true);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('touchstart', onDown, true);
    };
  }, [open, mobileDropdownOpen]);

  if (!open) return null;

  const setAudio = (patch: Partial<GameSettings['audio']>): void => {
    setDraft((prev) => ({ ...prev, audio: { ...prev.audio, ...patch } }));
  };

  const setGraphics = (patch: Partial<GameSettings['graphics']>): void => {
    setDraft((prev) => ({ ...prev, graphics: { ...prev.graphics, ...patch } }));
  };

  const setKeyboard = (patch: Partial<GameSettings['keyboard']>): void => {
    setDraft((prev) => ({ ...prev, keyboard: { ...prev.keyboard, ...patch } }));
  };

  const setGameplay = (patch: Partial<GameSettings['gameplay']>): void => {
    setDraft((prev) => ({ ...prev, gameplay: { ...prev.gameplay, ...patch } }));
  };

  const setLanguage = (patch: Partial<GameSettings['language']>): void => {
    setDraft((prev) => ({ ...prev, language: { ...prev.language, ...patch } }));
  };

  const setTutorial = (patch: Partial<GameSettings['tutorial']>): void => {
    setDraft((prev) => ({ ...prev, tutorial: { ...prev.tutorial, ...patch } }));
  };

  const canRevert = Boolean(persisted) && JSON.stringify(persisted) !== JSON.stringify(draft);

  return (
    <div
      className="settings-overlay"
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-label="Game settings"
      onMouseDown={(event) => {
        if (event.target === overlayRef.current) onClose();
      }}
    >
      <div className="settings-panel" ref={panelRef}>
        <div className="settings-topbar">
          <div className="settings-title">{section.toUpperCase()} SETTINGS</div>
          <div className="settings-user">
            <UserCircle2 size={18} />
            <span>{userLabel}</span>
          </div>
          <button type="button" className="settings-close" onClick={onClose} aria-label="Close settings">
            <X size={18} />
          </button>
        </div>

        <div className="settings-body">
          <div className="settings-nav-select-wrap" aria-label="Settings section">
            <div className="settings-nav-dropdown" ref={mobileDropdownRef}>
              <button
                className="settings-nav-trigger"
                type="button"
                aria-haspopup="listbox"
                aria-expanded={mobileDropdownOpen}
                onClick={() => setMobileDropdownOpen((v) => !v)}
              >
                <span className="settings-nav-trigger-label">{section}</span>
                <span className="settings-nav-trigger-caret" aria-hidden="true" />
              </button>
              {mobileDropdownOpen ? (
                <div className="settings-nav-menu" role="listbox" aria-label="Settings sections">
                  {SETTINGS_SECTIONS.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      role="option"
                      aria-selected={opt === section}
                      className={`settings-nav-option ${opt === section ? 'active' : ''}`.trim()}
                      onClick={() => {
                        setSection(opt);
                        setMobileDropdownOpen(false);
                      }}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          <aside className="settings-nav" aria-label="Settings sections">
            {SETTINGS_SECTIONS.map((opt) => sectionButton(opt, section === opt, () => setSection(opt)))}
          </aside>

          <main className="settings-main">
            {section === 'Graphics' ? (
              <>
                <div className="settings-section-heading">UI</div>
                <div className="settings-grid">
                  <label className="settings-row">
                    <span className="settings-label">UI Scale:</span>
                    <input
                      className="settings-range"
                      type="range"
                      min={80}
                      max={120}
                      value={draft.graphics.uiScalePct}
                      onChange={(e) => setGraphics({ uiScalePct: clampInt(Number(e.target.value), 80, 120) })}
                    />
                    <span className="settings-value">{draft.graphics.uiScalePct}%</span>
                  </label>
                </div>

                <div className="settings-section-heading mt">MOTION</div>
                <div className="settings-other">
                  <label className="settings-check">
                    <input
                      type="checkbox"
                      checked={draft.graphics.reduceMotion}
                      onChange={(e) => setGraphics({ reduceMotion: e.target.checked })}
                    />
                    <span>Reduce Motion</span>
                  </label>
                </div>
              </>
            ) : null}

            {section === 'Audio' ? (
              <>
                <div className="settings-section-heading">VOLUME</div>

                <div className="settings-grid">
                  <label className="settings-row">
                    <span className="settings-label">Master Volume:</span>
                    <input
                      className="settings-range"
                      type="range"
                      min={0}
                      max={100}
                      value={draft.audio.masterVolume}
                      onChange={(e) => setAudio({ masterVolume: clampInt(Number(e.target.value), 0, 100) })}
                    />
                    <span className="settings-value">{draft.audio.masterVolume}%</span>
                  </label>

                  <label className="settings-row">
                    <span className="settings-label">Object Music:</span>
                    <input
                      className="settings-range"
                      type="range"
                      min={0}
                      max={100}
                      value={draft.audio.objectMusic}
                      onChange={(e) => setAudio({ objectMusic: clampInt(Number(e.target.value), 0, 100) })}
                    />
                    <span className="settings-value">{draft.audio.objectMusic}%</span>
                  </label>

                  <label className="settings-row">
                    <span className="settings-label">Music:</span>
                    <input
                      className="settings-range"
                      type="range"
                      min={0}
                      max={100}
                      value={draft.audio.music}
                      onChange={(e) => setAudio({ music: clampInt(Number(e.target.value), 0, 100) })}
                    />
                    <span className="settings-value">{draft.audio.music}%</span>
                  </label>

                  <label className="settings-row">
                    <span className="settings-label">Voices:</span>
                    <input
                      className="settings-range"
                      type="range"
                      min={0}
                      max={100}
                      value={draft.audio.voices}
                      onChange={(e) => setAudio({ voices: clampInt(Number(e.target.value), 0, 100) })}
                    />
                    <span className="settings-value">{draft.audio.voices}%</span>
                  </label>

                  <label className="settings-row">
                    <span className="settings-label">Sound Effects:</span>
                    <input
                      className="settings-range"
                      type="range"
                      min={0}
                      max={100}
                      value={draft.audio.soundEffects}
                      onChange={(e) => setAudio({ soundEffects: clampInt(Number(e.target.value), 0, 100) })}
                    />
                    <span className="settings-value">{draft.audio.soundEffects}%</span>
                  </label>
                </div>

                <div className="settings-section-heading mt">OTHER</div>
                <div className="settings-other">
                  <label className="settings-check">
                    <input
                      type="checkbox"
                      checked={draft.audio.audioFocus}
                      onChange={(e) => setAudio({ audioFocus: e.target.checked })}
                    />
                    <span>Audio Focus</span>
                  </label>
                  <label className="settings-check">
                    <input
                      type="checkbox"
                      checked={draft.audio.enableMoodSting}
                      onChange={(e) => setAudio({ enableMoodSting: e.target.checked })}
                    />
                    <span>Enable Mood Sting</span>
                  </label>
                  <label className="settings-check">
                    <input
                      type="checkbox"
                      checked={draft.audio.matchSpeedStereoMusic}
                      onChange={(e) => setAudio({ matchSpeedStereoMusic: e.target.checked })}
                    />
                    <span>Match Speed Stereo Music</span>
                  </label>
                </div>
              </>
            ) : null}

            {section === 'Keyboard' ? (
              <>
                <div className="settings-section-heading">CONTROLS</div>
                <div className="settings-other">
                  <label className="settings-check">
                    <input
                      type="checkbox"
                      checked={draft.keyboard.enabled}
                      onChange={(e) => setKeyboard({ enabled: e.target.checked })}
                    />
                    <span>Enable Keyboard Shortcuts</span>
                  </label>
                </div>
                <div className="small text-muted mt-2">
                  Shortcuts: <strong>R</strong> restart, <strong>Esc</strong> settings, <strong>Ctrl + .</strong> (Windows) /{' '}
                  <strong>Cmd + .</strong> (Mac) AI takeover toggle.
                </div>
              </>
            ) : null}

            {section === 'Game Play' ? (
              <>
                <div className="settings-section-heading">AI</div>
                <div className="settings-grid">
                  <label className="settings-row">
                    <span className="settings-label">AI Think Delay:</span>
                    <input
                      className="settings-range"
                      type="range"
                      min={0}
                      max={2000}
                      step={50}
                      value={draft.gameplay.aiThinkMinMs}
                      onChange={(e) => setGameplay({ aiThinkMinMs: clampInt(Number(e.target.value), 0, 2000) })}
                    />
                    <span className="settings-value">{draft.gameplay.aiThinkMinMs}ms</span>
                  </label>
                </div>

                <div className="settings-section-heading mt">UI</div>
                <div className="settings-other">
                  <label className="settings-check">
                    <input
                      type="checkbox"
                      checked={draft.gameplay.showHints}
                      onChange={(e) => setGameplay({ showHints: e.target.checked })}
                    />
                    <span>Show Hints</span>
                  </label>
                </div>
              </>
            ) : null}

            {section === 'Language' ? (
              <>
                <div className="settings-section-heading">LANGUAGE</div>
                <label className="settings-row">
                  <span className="settings-label">Language:</span>
                  <select
                    className="settings-select"
                    value={draft.language.code}
                    onChange={(e) => setLanguage({ code: e.target.value })}
                  >
                    <option value="en">English</option>
                    <option value="af">Afrikaans</option>
                    <option value="ng">Oshiwambo</option>
                  </select>
                  <span className="settings-value" />
                </label>
                <div className="small text-muted mt-2">Language currently affects UI metadata only.</div>
              </>
            ) : null}

            {section === 'Tutorial & Tips' ? (
              <>
                <div className="settings-section-heading">TIPS</div>
                <div className="settings-other">
                  <label className="settings-check">
                    <input
                      type="checkbox"
                      checked={draft.tutorial.showTips}
                      onChange={(e) => setTutorial({ showTips: e.target.checked })}
                    />
                    <span>Show Tutorial Tips</span>
                  </label>
                </div>
              </>
            ) : null}
          </main>
        </div>

        <div className="settings-footer">
          <button
            type="button"
            className="settings-btn ghost"
            onClick={() => {
              setDraft(loadGameSettings());
            }}
            disabled={!canRevert}
          >
            Revert Changes
          </button>
          <button
            type="button"
            className="settings-btn primary"
            onClick={() => {
              saveGameSettings(draft);
              onApply?.(draft);
              onClose();
            }}
          >
            Apply Changes
          </button>
        </div>
      </div>
    </div>
  );
}
